import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import Notification from '../components/Admin/Notification';
import InventoryUploader from '../components/Shop/InventoryUploader';
import * as FiIcons from 'react-icons/fi';

const { FiLoader, FiCopy, FiRefreshCw, FiUploadCloud, FiDownload, FiUserMinus } = FiIcons;

const TEMPLATE_CSV =
  'Part Number,Price,Quantity,SKU,Bin,Cost,Description\r\nWB13K10026,24.50,4,A-12,B3,12.00,Oven igniter\r\n';

const inputClass =
  'w-full min-h-[44px] sm:min-h-0 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 px-3 py-2.5 text-base sm:text-sm text-slate-900 dark:text-white focus:border-blue-600 outline-none';

const downloadTemplate = () => {
  const url = URL.createObjectURL(new Blob([TEMPLATE_CSV], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'partscouter-price-stock-template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

const Shop = () => {
  const { refreshProfile } = useAuth();
  const [shop, setShop] = useState(undefined); // undefined = loading, null = not in a shop
  const [notify, setNotify] = useState(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api('myShop');
      setShop(res.shop);
    } catch (err) {
      setShop(null);
      setNotify({ message: err.message, type: 'error' });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Run an action, then reload; failures show as a notice instead of breaking the page.
  const run = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      if (success) setNotify({ message: success, type: 'success' });
      await Promise.all([load(), refreshProfile()]);
    } catch (err) {
      setNotify({ message: err.message, type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotify({ message: 'Copied.', type: 'success' });
    } catch {
      setNotify({ message: 'Could not copy. Select the code and copy it by hand.', type: 'error' });
    }
  };

  if (shop === undefined) {
    return (
      <div className="flex items-center justify-center gap-3 py-24 text-sm text-slate-500">
        <SafeIcon icon={FiLoader} className="animate-spin" /> Loading your shop&hellip;
      </div>
    );
  }

  const isOwner = shop?.role === 'owner';

  return (
    <div className="mx-auto max-w-3xl space-y-5 text-left">
      {notify && <Notification {...notify} onClose={() => setNotify(null)} />}

      <div>
        <div className="caption mb-1">Shop</div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{shop ? shop.name : 'Set up your shop'}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {shop
            ? 'Everyone in this shop sees the same prices and stock next to each part.'
            : 'A shop shares one price and stock list between its counter staff and technicians.'}
        </p>
      </div>

      {!shop && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <form className="panel space-y-3 p-5" onSubmit={(e) => { e.preventDefault(); run(() => api('createShop', { name }), 'Shop created.'); }}>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Create a shop</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">You become the owner: you upload the list and invite people.</p>
            <label htmlFor="shop-name" className="caption block">Shop name</label>
            <input id="shop-name" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Appliance Parts" required minLength={2} maxLength={80} />
            <button type="submit" className="btn-primary w-full" disabled={busy}>Create shop</button>
          </form>

          <form className="panel space-y-3 p-5" onSubmit={(e) => { e.preventDefault(); run(() => api('joinShop', { code }), 'You joined the shop.'); }}>
            <h2 className="text-base font-semibold text-slate-900 dark:text-white">Join a shop</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">Ask the shop owner for the 8-character join code.</p>
            <label htmlFor="join-code" className="caption block">Join code</label>
            <input id="join-code" className={`${inputClass} font-mono uppercase tracking-widest`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-2345" required maxLength={12} autoCapitalize="characters" autoComplete="off" />
            <button type="submit" className="btn-secondary w-full" disabled={busy}>Join shop</button>
          </form>
        </div>
      )}

      {shop && (
        <>
          <section className="panel p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="caption mb-1">Price and stock list</div>
                <p className="text-sm text-slate-800 dark:text-slate-200">
                  {shop.item_count > 0
                    ? <><span className="font-mono font-semibold">{shop.item_count.toLocaleString()}</span> items{shop.inventory_updated_at && <>, updated {format(new Date(shop.inventory_updated_at.replace(' ', 'T') + 'Z'), 'MMM d, yyyy')}</>}</>
                    : 'No list uploaded yet.'}
                </p>
              </div>
              {isOwner && (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                  <button type="button" className="btn-secondary" onClick={downloadTemplate}><SafeIcon icon={FiDownload} /> Template</button>
                  <button type="button" className="btn-primary" onClick={() => setShowUpload(true)}><SafeIcon icon={FiUploadCloud} /> {shop.item_count > 0 ? 'Replace list' : 'Upload list'}</button>
                </div>
              )}
            </div>
            {!isOwner && <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">Only the shop owner can upload a new list.</p>}
            {isOwner && (
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                Uploading replaces the whole list. Include every part you stock, with your part number or any alternate-brand number; matches are made ignoring dashes and spaces.
              </p>
            )}
          </section>

          {isOwner && (
            <>
              <section className="panel p-5">
                <div className="caption mb-2">Join code</div>
                <div className="flex flex-wrap items-center gap-3">
                  <code className="bg-slate-100 px-3 py-2 font-mono text-lg font-semibold tracking-widest text-slate-900 dark:bg-slate-800 dark:text-white">
                    {shop.join_code.slice(0, 4)}-{shop.join_code.slice(4)}
                  </code>
                  <button type="button" className="btn-secondary" onClick={() => copy(shop.join_code)}><SafeIcon icon={FiCopy} /> Copy</button>
                  <button type="button" className="btn-secondary" disabled={busy} onClick={() => run(() => api('rotateJoinCode'), 'New join code created. The old one no longer works.')}>
                    <SafeIcon icon={FiRefreshCw} /> New code
                  </button>
                </div>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">Share this code with people who should join. Make a new code if it gets passed around.</p>
              </section>

              <section className="panel">
                <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-800"><div className="caption">People ({shop.members.length})</div></div>
                <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                  {shop.members.map((m) => (
                    <li key={m.user_id} className="flex items-center justify-between gap-3 px-5 py-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">{m.name || m.email}</div>
                        <div className="truncate text-xs text-slate-500">{m.name ? m.email : ''}{m.name ? ' · ' : ''}{m.role === 'owner' ? 'Owner' : 'Member'}</div>
                      </div>
                      {m.role !== 'owner' && (
                        <button type="button" disabled={busy} className="btn-secondary !px-3 text-xs" onClick={() => { if (window.confirm(`Remove ${m.name || m.email} from the shop?`)) run(() => api('removeMember', { user_id: m.user_id }), 'Removed.'); }}>
                          <SafeIcon icon={FiUserMinus} /> Remove
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}

          {!isOwner && (
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => { if (window.confirm('Leave this shop? You will stop seeing its prices and stock.')) run(() => api('leaveShop'), 'You left the shop.'); }}>
              Leave shop
            </button>
          )}
        </>
      )}

      {showUpload && (
        <InventoryUploader currentCount={shop?.item_count || 0} onClose={() => setShowUpload(false)} onDone={load} />
      )}
    </div>
  );
};

export default Shop;
